<template>
  <div id="accounts-payable">
    <BreadCrumb :items="breadcrumbItems" />

    <v-card elevation="1" class="pa-2">
      <v-card-title class="header d-flex justify-space-between align-center">
        <h1 class="pa-0">Adicionais</h1>
        <div>
          <v-btn @click="handleExport" :loading="isExportButtonLoading" elevation="2" color="info" class="mr-2" large>
            Exportar planilha</v-btn
          >
          <v-btn @click="openAditionalForm" elevation="2" color="success" large> Novo adicional </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-expansion-panels accordion class="mt-2 mb-6">
          <v-expansion-panel class="elevation-1">
            <v-expansion-panel-header color="#f2f2f2" class="px-4">
              <span>Filtrar por</span>
              <template v-slot:actions>
                <v-icon color="black"> mdi-chevron-down </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2 pb-1">
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="isInitialDateVisible"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedInitialDate"
                        label="Data inicial"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="initialDate" locale="pt-br"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="isEndDateVisible"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        label="Data final"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="endDate" locale="pt-br"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="isDiscount"
                    :items="isDiscountItems"
                    item-value="value"
                    label="Tipo de adicional"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="stopId" label="Id da parada" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="boardingId" label="Id do embarque" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="isDirectDeliverer"
                    :items="isDirectDelivererItems"
                    item-value="value"
                    label="Tipo de usuário"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    :disabled="!hasIsDirectDeliverer"
                    v-model="aditionalUser"
                    :items="selectedAditionalUserTypeItems"
                    item-text="name"
                    return-object
                    :label="aditionalUserLabel"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="linkedPaymentOrder"
                    :items="linkedPaymentOrderItems"
                    item-value="value"
                    label="Ordem de pagamento"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="nfeNumber"
                    v-mask="'############################################'"
                    label="Número da NFE"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="senderId"
                    :items="senders"
                    item-text="name"
                    item-value="id"
                    label="Remetente"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="isLink" :items="isLinkItems" item-value="value" label="Avaria Operacional" />
                </v-col>
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn
                    @click="clearFilters"
                    :disabled="shouldDisableFilterButtons"
                    :dark="!shouldDisableFilterButtons"
                    class="mx-2"
                    color="red"
                    >LIMPAR FILTROS</v-btn
                  >
                  <v-btn
                    @click="searchByFilters"
                    :loading="isLoading"
                    :disabled="shouldDisableFilterButtons"
                    :dark="!shouldDisableFilterButtons"
                    color="green"
                    >BUSCAR</v-btn
                  >
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-data-table
        class="px-5"
        :headers="aditionalsHeaders"
        :items="formattedAditionals"
        :items-per-page="pageSize"
        @update:items-per-page="changeItemsPerPage"
        :loading="isLoading"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 50],
          itemsPerPageText: 'Itens por página'
        }"
      >
        <template v-slot:[`item.boardingOrStopId`]="{ item }">
          <copy-to-clipboard-tooltip v-if="item.boardingOrStopId != null" :text="item.boardingOrStopId" should-copy />
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.price`]="{ item }">
          <span> {{ getFormattedPrice(item.price) }}</span>
        </template>
        <template v-slot:[`item.isDiscount`]="{ item }">
          <span> {{ getAditionalType(item.isDiscount) }}</span>
        </template>
        <template v-slot:[`item.isLink`]="{ item }">
          <span> {{ getIsLink(item.isLink) }}</span>
        </template>
        <template v-slot:[`item.additionalDate`]="{ item }">
          <span> {{ getFormattedDate(item.additionalDate) }}</span>
        </template>
        <template v-slot:[`item.name`]="{ item }">
          <span> {{ getUserName(item.name) }}</span>
        </template>
        <template v-slot:[`item.observation`]="{ item }">
          <copy-to-clipboard-tooltip v-if="item.observation != null" :text="item.observation" />

          <span v-else>-</span>
        </template>
        <template v-slot:[`item.paymentOrderId`]="{ item }">
          <span>
            <v-chip :color="getChipColor(item.paymentOrderId)" dark>
              {{ getChipLabel(item.paymentOrderId) }}
            </v-chip>
          </span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-center">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="black" dense v-bind="attrs" v-on="on"> mdi-menu </v-icon>
              </template>
              <v-list>
                <v-list-item @click="handleAditionalToEdit(item)">
                  <v-list-item-title>{{ getViewOrEditLabel(item) }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!hasPaymentOrder(item.paymentOrderId)" @click="handleAditionalToDelete(item)">
                  <v-list-item-title style="color: red">Deletar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
      <v-pagination v-model="pageNumber" :length="totalPages" :total-visible="7"></v-pagination>
    </v-card>
    <delete-confirm-dialog
      :isOpen.sync="isDeleteDialogOpen"
      @confirmClick="handleDeleteAditional"
      :isConfirmBtnLoading="isDeleteButtonLoading"
      title="Deletar Adicional"
      body-text="Você confirma deletar este adicional?"
    />
    <aditional-edit-form
      :isOpen.sync="isEditionDialogOpen"
      :aditionalToEdit="aditionalToEdit"
      @shouldUpdateList="getAditionals"
    />
    <aditional-form-dialog :isOpen.sync="isAditionalFormOpen" @shouldUpdateList="getAditionals" />
    <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
  </div>
</template>

<script>
import BreadCrumb from '@/components/BreadCrumb.vue';
import AditionalFormDialog from '@/components/AccountsPayable/AditionalFormDialog.vue';
import AditionalEditForm from '@/components/AccountsPayable/AditionalEditForm.vue';
import { mapActions } from 'vuex';
import { formatDate } from '@/handlers/DateHandler';
import aditionalUsersType from '@/consts/AditionalUsersType';
import DeleteConfirmDialog from '@/components/ConfirmDialog';
import MessagesError from '@/components/MessagesError.vue';
import CopyToClipboardTooltip from '@/components/CopyToClipboardTooltip.vue';

export default {
  components: {
    BreadCrumb,
    AditionalFormDialog,
    DeleteConfirmDialog,
    MessagesError,
    AditionalEditForm,
    CopyToClipboardTooltip
  },
  data: () => ({
    aditionalUsersType: aditionalUsersType,
    isEditionDialogOpen: false,
    isDeleteDialogOpen: false,
    isDeleteButtonLoading: false,
    isExportButtonLoading: false,
    aditionalToDelete: null,
    aditionalToEdit: {
      id: null,
      applicationUserId: null,
      isDirectDeliverer: null,
      isDiscount: null,
      price: null,
      additionalDate: null,
      observation: null,
      boardingId: null,
      stopId: null,
      justificationId: null
    },
    pageNumber: 1,
    pageSize: 10,
    totalPages: 10,
    totalRecords: null,
    isLoading: true,
    isAditionalFormOpen: false,
    aditionals: [],
    initialDate: null,
    endDate: null,
    isDiscount: null,
    stopId: null,
    boardingId: null,
    isDirectDeliverer: null,
    aditionalUser: null,
    linkedPaymentOrder: null,
    nfeNumber: null,
    senderId: null,
    isLink: null,
    transhipmentUsers: [],
    directDeliveresUsers: [],
    senders: [],
    errors: [],
    errorsType: 'error',
    error500: null,
    isLinkItems: [
      { text: 'Sim', value: true },
      { text: 'Não', value: false }
    ],
    isDirectDelivererItems: [
      { text: 'Entregador direto', value: true },
      { text: 'Transbordo', value: false }
    ],
    linkedPaymentOrderItems: [
      { text: 'Vinculado', value: true },
      { text: 'Não vinculado', value: false }
    ],
    isDiscountItems: [
      { text: 'Crédito', value: false },
      { text: 'Débito', value: true }
    ],
    aditionalsHeaders: [
      {
        value: 'name',
        text: 'USUÁRIO',
        sortable: false
      },
      {
        value: 'typeUser',
        text: 'TIPO DE USUÁRIO',
        sortable: false
      },
      {
        value: 'isDiscount',
        text: 'TIPO',
        sortable: false
      },
      {
        value: 'isLink',
        text: 'AVARIA',
        sortable: false
      },
      {
        value: 'boardingOrStopId',
        text: 'ID EMBARQUE/PARADA',
        sortable: false
      },
      {
        value: 'nroNfe',
        text: 'NFE',
        sortable: false
      },
      {
        value: 'additionalDate',
        text: 'DATA DE CRIAÇÃO',
        sortable: false
      },
      {
        value: 'price',
        text: 'VALOR',
        sortable: false
      },
      {
        value: 'justification',
        text: 'JUSTIFICATIVA',
        sortable: false
      },
      {
        value: 'nameSender',
        text: 'REMETENTE',
        sortable: false
      },
      {
        value: 'observation',
        text: 'OBSERVAÇÃO',
        sortable: false
      },
      {
        value: 'paymentOrderId',
        text: 'ORDEM DE PAGAMENTO',
        align: 'center',
        sortable: false
      },
      {
        value: 'actions',
        text: 'AÇÕES',
        sortable: false
      }
    ],
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Financeiro',
        disabled: true,
        to: '/couriers-accounts-payable/'
      },
      {
        text: 'Adicionais',
        disabled: true
      }
    ],
    isInitialDateVisible: false,
    isEndDateVisible: false
  }),

  computed: {
    formattedAditionals() {
      return this.aditionals.length > 0
        ? this.aditionals.map((aditional) => {
            const boardingOrStopId = this.getBoardingOrStopId(aditional);
            return { ...aditional, boardingOrStopId };
          })
        : [];
    },
    formattedInitialDate() {
      const date = this.initialDate;
      return date ? formatDate(date) : null;
    },
    formattedEndDate() {
      const date = this.endDate;
      return date ? formatDate(date) : null;
    },
    hasIsDirectDeliverer() {
      return this.isDirectDeliverer != null;
    },
    selectedAditionalUserTypeItems() {
      if (this.hasIsDirectDeliverer) {
        return this.isDirectDeliverer ? this.directDeliveresUsers : this.transhipmentUsers;
      } else return [];
    },
    aditionalUserLabel() {
      if (this.hasIsDirectDeliverer) {
        return this.isDirectDeliverer ? 'Entregador direto' : 'Transbordo';
      } else return 'Selecione um tipo';
    },
    shouldDisableFilterButtons() {
      return (
        !this.initialDate &&
        !this.endDate &&
        this.isDiscount === null &&
        !this.stopId &&
        !this.boardingId &&
        this.isDirectDeliverer === null &&
        !this.aditionalUser &&
        this.linkedPaymentOrder === null &&
        !this.nfeNumber &&
        !this.senderId &&
        this.isLink === null
      );
    },

    getTheLastThirtyDays() {
      const now = new Date();
      const currentDay = now;
      const thirtyDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);

      return {
        thirtyDayAgo: thirtyDayAgo.toISOString().slice(0, 10),
        currentDay: currentDay.toISOString().slice(0, 10)
      };
    }
  },

  watch: {
    pageNumber: {
      handler() {
        this.getAditionals();
      }
    },
    isDirectDeliverer: {
      async handler(isDirectDeliverer) {
        this.aditionalUser = null;
        if (isDirectDeliverer && this.directDeliveresUsers.length === 0) {
          this.directDeliveresUsers = await this.getDirectDeliverersForPaymentOrder();
        } else if (!isDirectDeliverer && this.transhipmentUsers.length === 0) {
          this.transhipmentUsers = await this.getTranshipmentForPaymentOrder();
        }
      }
    }
  },

  async created() {
    this.filterTheLastThirtyDays();
    await this.getAditionals();
    await this.getAllSenders();
  },

  methods: {
    ...mapActions('AccountsStore', [
      'getAllAditionals',
      'getDirectDeliverersForPaymentOrder',
      'getTranshipmentForPaymentOrder',
      'deleteAditional',
      'exportAditionalSheet',
      'getSenders'
    ]),

    async getAditionals() {
      try {
        this.isLoading = true;
        const filters = this.getFilters();
        const { data, totalPages, totalRecords } = await this.getAllAditionals(filters);
        this.aditionals = data;
        this.totalPages = totalPages;
        this.totalRecords = totalRecords;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    filterTheLastThirtyDays() {
      const { thirtyDayAgo, currentDay } = this.getTheLastThirtyDays;
      this.initialDate = thirtyDayAgo;
      this.endDate = currentDay;
    },

    async handleDeleteAditional() {
      try {
        const { id } = this.aditionalToDelete;
        this.isDeleteButtonLoading = true;
        const data = await this.deleteAditional(id);
        if (data.success) {
          this.handleSuccessFeedback();
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
          this.isDeleteDialogOpen = false;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isDeleteButtonLoading = false;
      }
    },

    async handleExport() {
      try {
        this.isExportButtonLoading = true;
        await this.getAditionals();
        const filters = this.getFilters();
        const data = await this.exportAditionalSheet({ ...filters, pageSize: this.totalRecords });
        console.log(data);
        if (data.success) {
          this.errors = ['Planilha exportada com sucesso!'];
          this.errorsType = 'success';
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isExportButtonLoading = false;
      }
    },

    async getAllSenders() {
      try {
        this.senders = await this.getSenders();
      } catch (error) {
        console.error(error);
      }
    },

    searchByFilters() {
      this.pageNumber = 1;
      this.getAditionals();
    },

    getFilters() {
      return {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        initialDate: this.initialDate,
        endDate: this.endDate,
        isDiscount: this.isDiscount,
        stopId: this.stopId,
        boardingId: this.boardingId,
        isDirectDeliverer: this.isDirectDeliverer,
        applicationUserId: this.aditionalUser ? this.aditionalUser.aspNetUserId : null,
        linkedPaymentOrder: this.linkedPaymentOrder,
        nfeNumber: this.nfeNumber,
        senderId: this.senderId,
        isLink: this.isLink
      };
    },

    handleSuccessFeedback() {
      this.errors = ['Adicional deletado com sucesso!'];
      this.errorsType = 'success';
      this.isDeleteDialogOpen = false;
      this.getAditionals();
    },

    handleAditionalToDelete(aditional) {
      this.isDeleteDialogOpen = true;
      this.aditionalToDelete = aditional;
    },

    handleAditionalToEdit(aditional) {
      this.isEditionDialogOpen = true;
      this.aditionalToEdit = aditional;
    },

    clearFilters() {
      this.initialDate = null;
      this.endDate = null;
      this.isDiscount = null;
      this.stopId = null;
      this.boardingId = null;
      this.isDirectDeliverer = null;
      this.aditionalUser = null;
      this.linkedPaymentOrder = null;
      this.nfeNumber = null;
      this.senderId = null;
      this.isLink = null;
      this.getAditionals();
    },

    getChipColor(paymentOrderId) {
      return this.hasPaymentOrder(paymentOrderId) ? 'green' : 'red';
    },

    getChipLabel(paymentOrderId) {
      return this.hasPaymentOrder(paymentOrderId) ? 'Vinculado' : 'Não vinculado';
    },

    getViewOrEditLabel(item) {
      return this.hasPaymentOrder(item.paymentOrderId) ? 'Visualizar' : 'Editar';
    },

    getAditionalType(isDiscount) {
      return isDiscount ? 'Débito' : 'Crédito';
    },

    getIsLink(isLink) {
      return isLink ? 'Sim' : 'Não';
    },

    getFormattedPrice(price) {
      return price.toLocaleString('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' });
    },

    getBoardingOrStopId(adicional) {
      if (adicional.boardingId) return adicional.boardingId;
      else if (adicional.stopId) return adicional.stopId;
      else return null;
    },

    getFormattedDate(date) {
      return formatDate(date);
    },

    getUserName(name) {
      return name ? name : '-';
    },

    openAditionalForm() {
      this.isAditionalFormOpen = true;
    },

    changeItemsPerPage(items) {
      this.pageSize = items;
      this.getAditionals();
    },

    hasPaymentOrder(paymentOrderId) {
      return paymentOrderId !== '00000000-0000-0000-0000-000000000000';
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-input__slot {
  margin: 0;
  padding: 0;

  &::after,
  &::before {
    display: none !important;
  }
}
.aditional-observation {
  width: 100px;
  display: inline-block;
}
</style>
